import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { CoinsIcon } from '@phosphor-icons/react'

import { Card, useFetch } from '@reveni-io/reveni-ui'

import billingServices from '@/billing/services/services'
import TitlePage from '@/common/components/TitlePage/TitlePage'
import AppContext from '@/common/context/app.context'
import useFilters from '@/common/hooks/useFilters/useFilters'

import BillingAnalytics from './BillingAnalytics/BillingAnalytics'
import BillingReports from './BillingReports/BillingReports'
import Filters from './Filters/Filters'
import { filterNames, filters } from './constants'
import { getNextInvoiceAmount } from './helpers'
import * as Styles from './styles'

const BillingPage = () => {
  const { t } = useTranslation()
  const { info } = useContext(AppContext)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const {
    data: reports,
    loading: loadingReports,
    fetchData: getReports,
  } = useFetch(billingServices.getBillingReports, { doInitialRequest: false })

  const { data: balance, loading: loadingBalance } = useFetch(billingServices.getBillingBalance, {
    doInitialRequest: true,
    params: { storeId: info.selectedStore.id },
  })
  const {
    updateFilter,
    invoiceDateStart,
    invoiceDateEnd,
    status,
    query,
    page,
    orderBy,
    refetch,
    type,
    refetchWithoutFilters,
    collectedDateStart,
    collectedDateEnd,
  } = useFilters({
    filters: filters,
    doInitialRequest: true,
    fetchData: params =>
      getReports({ storeId: info.selectedStore.id, returnPortalId: info.selectedReturnPortal.id, params }),
    filterValues: {},
    nameOfFiltersInContext: 'billingFilters',
  })

  const navigation = useNavigate()
  const location = useLocation()

  const onChangeFilter = (type, data, doRequest) => {
    updateFilter(type, data, doRequest)
  }

  const handleChangePage = page => onChangeFilter(filterNames.page, page)

  const onSelectInvoice = invoiceId => {
    navigation(`/billing/${invoiceId}`, { state: { from: location } })
  }

  const handleClickInStat = stat => {
    if (stat === 'pendingInvoices') {
      onChangeFilter(filterNames.status, ['pending'], true)
    }
  }

  return (
    <Styles.MainContainer>
      <TitlePage Icon={CoinsIcon}>
        {t('Facturas')} - {info?.selectedStore?.name}
      </TitlePage>
      {!loadingBalance && (
        <BillingAnalytics
          currency={info?.selectedStore?.currency || 'EUR'}
          pendingInvoices={balance?.pending_invoices}
          pendingInvoicesAmount={balance?.pending_invoices_amount}
          nextInvoiceDate={balance?.next_invoice_date}
          nextInvoiceAmount={getNextInvoiceAmount(balance?.next_invoice_base_amount, balance?.next_invoice_fee_amount)}
          nextInvoiceInstantAmount={balance?.next_invoice_base_amount}
          nextInvoiceFeeAmount={Number(balance?.next_invoice_fee_amount)}
          onClickInStat={handleClickInStat}
        />
      )}
      <Card>
        <Filters
          onChangeFilter={onChangeFilter}
          filters={{
            invoiceDates: { startDate: invoiceDateStart, endDate: invoiceDateEnd },
            collectedDates: { startDate: collectedDateStart, endDate: collectedDateEnd },
            billingStatus: status,
            billingType: type,
          }}
          query={query}
          toggleShowFiltersModal={setShowFiltersModal}
          showFiltersModal={showFiltersModal}
          onApplyFilters={refetch}
          onClearFilters={refetchWithoutFilters}
          orderBy={orderBy}
        />
        <BillingReports
          loadingReports={loadingReports}
          reports={reports}
          page={page}
          onSelectInvoice={onSelectInvoice}
          onChangePage={handleChangePage}
        />
      </Card>
    </Styles.MainContainer>
  )
}

export default BillingPage
